import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Layers, Zap, CheckCircle2, FileText, Calculator, MonitorPlay } from 'lucide-react';
import { BriefingModal } from './BriefingModal';

const products = {
  'deal-aos': {
    title: 'Deal OS™',
    subtitle: 'The Autonomous Operating System',
    tag: '100% In-Tenant VPC',
    sections: [
      {
        heading: 'Overview & Architecture',
        icon: Layers,
        points: [
          'Serves as the full-stack, enterprise-grade cognitive operating system underpinning the entire M&A and investment deal lifecycle.',
          'Built as a 4-tier modular architecture spanning an executive interface layer (DealSpace™), autonomous orchestration (SmartOrch™), unified institutional memory (OmniContext™), and domain-tuned SLMs (StrategyCore™).',
          'Designed to decouple fund AUM and advisory practice margins from human analyst bandwidth, driving up to a 10x throughput unlock.',
        ],
      },
      {
        heading: 'Security & Infrastructure',
        icon: Shield,
        points: [
          '100% Sovereign In-Tenant Deployment: Deploys completely air-gapped within the enterprise\'s own cloud perimeter (Microsoft Azure, AWS, GCP, or sovereign clouds like Core42).',
          'Zero MNPI Retention: Guarantees zero leakage or model retention of Material Non-Public Information, preserving absolute fund confidentiality.',
          'Zero Vendor Lock-in: Model-agnostic runtime capable of routing and orchestrating foundational models (Claude, OpenAI) alongside proprietary, calibrated domain SLMs.',
        ],
      },
      {
        heading: 'Institutional Impact',
        icon: Zap,
        points: [
          'Acts as an unbroken, continuous runtime that links raw data ingestion directly to final Investment Committee (IC) decisions without context loss or drift.',
        ],
      },
    ],
  },
  'workflows': {
    title: 'Workflows',
    subtitle: 'Autonomous Transaction Execution',
    tag: 'Multi-Agent DAG / 48h',
    sections: [
      {
        heading: 'Multi-Agent DAG Choreography',
        icon: Layers,
        points: [
          'Replaces static analytical sprints and manual analyst assembly lines with autonomous Directed Acyclic Graphs (SmartOrch™).',
          'Agents concurrently plan, reconcile, cross-examine, and stress-test target assets, transforming multi-week workstreams into 48-hour audit-ready verdicts.',
        ],
      },
      {
        heading: 'Lifecycle Coverage',
        icon: Zap,
        points: [
          'Sourcing & Origination: Eliminates backward-looking manual scans to evaluate disruptive business models and underwrite deep-tech or frontier sectors outside standard partner comfort zones.',
          'Commercial Due Diligence (CDD): Replaces external consulting retainers (often costing upwards of $400k) with autonomous 48-hour CDD runs.',
          'Value Creation & PortCo Tracking: Automates post-close execution, monitoring portfolio company telemetry, identifying strategic synergies, and formulating add-on or exit positioning.',
        ],
      },
      {
        heading: 'Adversarial & Deliberation Protocols',
        icon: Shield,
        points: [
          'Executes systematic Bull vs. Bear debate frameworks and founder interrogation routines to uncover moat vulnerabilities and strategic blind spots before committee review.',
        ],
      },
    ],
  },
  'studio': {
    title: 'Studio',
    subtitle: 'The Unified Decision Cockpit',
    tag: '18-Factor IC Rubric',
    sections: [
      {
        heading: 'Executive Interface & Synthesis',
        icon: MonitorPlay,
        points: [
          'Functions as the central decision surface (DealSpace™) tailored for Partners, Managing Directors, and Investment Committees.',
          'Eliminates the traditional tool sprawl of fragmented spreadsheets, disparate market research terminals, and isolated copilots.',
          'Delivers zero-prompt, context-aware interaction surfaces that convert massive data pools into clear, board-level strategic verdicts.',
        ],
      },
      {
        heading: 'Custom Governance & Rubric Alignment',
        icon: Shield,
        points: [
          'Hardwires the firm\'s proprietary investment governance directly into the platform, evaluating target deals against custom multi-factor IC scoring rubrics, hurdles, and risk mandates.',
          'Allows real-time interactive navigation through scenario trees, valuation sensitivities, and competitive moat hypotheses.',
        ],
      },
      {
        heading: 'Enterprise Activation',
        icon: Zap,
        points: [
          'Fully operational across both buy-side entities (Private Equity, Sovereign Wealth, Corporate Venture) and sell-side advisory desks (Strategy Consulting, Investment Banks).',
        ],
      },
    ],
  },
  'memo-model': {
    title: 'Memo & Model',
    subtitle: 'Deterministic Synthesis & Deliverables',
    tag: 'Off-LLM Deterministic Math',
    sections: [
      {
        heading: 'Deterministic Math Engine',
        icon: Calculator,
        points: [
          'Employs isolated, off-LLM execution engines for all complex numerical modeling, valuation constructs, debt schedules, returns, and scenario matrices.',
          'Eliminates LLM calculation hallucinations to deliver mathematically verifiable figures (such as exact IRR, MOIC, and debt coverage).',
        ],
      },
      {
        heading: 'Cell-to-Source Forensic Lineage',
        icon: FileText,
        points: [
          'Every figure, metric, and qualitative claim in an IC memo is back-linked with bidirectional provenance to the exact source cell, PDF page, or data room transcript via OmniContext™.',
          'Ingests complex, multi-format Virtual Data Rooms (VDRs), audited filings, and proprietary industry datasets without manual data extraction bottlenecks.',
        ],
      },
      {
        heading: 'Publication-Grade Artifacts',
        icon: Zap,
        points: [
          'Outputs institutional-grade, audit-ready deliverables (IC Memos, Board Decks, CDD Reports) formatted directly to the firm\'s bespoke brand guidelines and structural templates.',
          'Compresses weeks of manual production into ready-to-deliberate assets on Day 1.',
        ],
      },
    ],
  },
};

export const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = products[slug];
  const [briefingOpen, setBriefingOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0F172A] mb-4">Product not found</h1>
          <Link to="/" className="text-sm font-medium text-slate-500 hover:text-[#0F172A] transition">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#090E1A]">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 pt-10 pb-14 md:pt-12 md:pb-16">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 text-white/50 hover:text-white hover:border-white/30 transition mb-8"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="mb-3">
            <span className="inline-block text-xs uppercase tracking-[0.2em] text-white/35 font-medium border border-white/15 rounded-full px-4 py-1.5">
              {product.tag}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight mb-3">
            {product.title}
          </h1>
          <p className="text-base md:text-lg text-white/50 font-light">
            {product.subtitle}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[900px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="space-y-16">
          {product.sections.map((section, idx) => {
            const Icon = section.icon;
            return (
              <div key={idx}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                    <Icon size={20} className="text-[#0F172A]" />
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] tracking-tight">
                    {section.heading}
                  </h2>
                </div>
                <ul className="space-y-4 pl-[52px]">
                  {section.points.map((point, j) => (
                    <li key={j} className="flex gap-3">
                      <CheckCircle2 size={18} className="text-slate-300 shrink-0 mt-0.5" />
                      <p className="text-[15px] text-slate-600 leading-relaxed">{point}</p>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-20 pt-12 border-t border-slate-200">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3 className="text-lg font-bold text-[#0F172A] mb-1">Ready to see {product.title} in action?</h3>
              <p className="text-sm text-slate-500">Schedule a confidential briefing with a Frontier partner.</p>
            </div>
            <button
              onClick={() => setBriefingOpen(true)}
              className="shrink-0 flex items-center gap-2 bg-[#090E1A] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-slate-800 transition"
            >
              Request Briefing <ArrowLeft size={14} className="rotate-180" />
            </button>
          </div>
        </div>
      </div>

      <BriefingModal
        isOpen={briefingOpen}
        onClose={() => setBriefingOpen(false)}
        inquiryType="Platform Demo & Capabilities Briefing"
      />
    </div>
  );
};
